import { useEffect, useRef } from "react"
import { useMap, useMapsLibrary } from "@vis.gl/react-google-maps"

type Props = {
  points?: { lat: number; lng: number }[]
  editable?: boolean
  fill: string
  draggable?: boolean
  onClick?: () => void
  onPolygonRef?: (polygon: google.maps.Polygon) => void
}

const latOffset = 0.002
const lngOffset = 0.002

export function Polygon({ points, editable, draggable, fill, onClick, onPolygonRef }: Props) {
  const map = useMap()
  const maps = useMapsLibrary("maps")
  const pointsRef = useRef(points)
  const onClickRef = useRef(onClick)
  const onPolygonRefRef = useRef(onPolygonRef)
  const polygonRef = useRef<google.maps.Polygon | null>(null)
  onClickRef.current = onClick
  onPolygonRefRef.current = onPolygonRef
  if (points) pointsRef.current = points

  useEffect(() => {
    if (!maps || !map) return

    const center = map.getCenter()
    const lat = center?.lat() ?? 0
    const lng = center?.lng() ?? 0

    const initialPaths = pointsRef.current ?? [
      { lat: lat + latOffset, lng: lng - lngOffset },
      { lat: lat + latOffset, lng: lng + lngOffset },
      { lat: lat - latOffset, lng: lng + lngOffset },
      { lat: lat - latOffset, lng: lng - lngOffset },
    ]

    const polygon = new maps.Polygon({
      map,
      paths: initialPaths,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillOpacity: 0.35,
      fillColor: fill,
      strokeColor: fill,
      editable,
      draggable,
      zIndex: editable ? 10 : 1,
      clickable: true,
    })
    polygonRef.current = polygon

    const clickListener = onClickRef.current ? polygon.addListener("click", () => onClickRef.current?.()) : null

    if (editable && onPolygonRefRef.current) {
      onPolygonRefRef.current(polygon)
    }

    return () => {
      polygon.setMap(null)
      clickListener?.remove()
      polygonRef.current = null
    }
  }, [maps, map])

  useEffect(() => {
    if (polygonRef.current && fill) {
      polygonRef.current.setOptions({ fillColor: fill, strokeColor: fill })
    }
  }, [fill])

  return null
}
